import type { FeaturePrototype } from '../prototype/FeaturePrototype';
import {
    collectGeometryObjectSourceReferenceIds,
    collectCurrentFeatureResultObjectIds,
    isGeometryObjectDirectResultOfFeature,
    readCommandStringParam,
    uniqueNonEmptyStrings,
} from '../../utils/feature/featureCommandDependencyUtils';

/**
 * 特征命令依赖解析上下文。
 */
export interface FeatureCommandDependencyContext<TPrototype extends FeaturePrototype = FeaturePrototype> {
    /** 待解析的命令。 */
    readonly command: CadCommand;
    /** 命令恢复出的特征原型。 */
    readonly prototype: TPrototype;
    /** 当前 Worker 对象摘要，用于补充多结果体产物 ID。 */
    readonly objects: readonly GeometryObject[];
    /** 当前特征之前的正式命令，仅用于派生范围重建依赖。 */
    readonly commands?: readonly CadCommand[];
    /** 当前命令在共享历史中的位置，避免为每个特征复制前缀。 */
    readonly commandIndex?: number;
}

/**
 * 特征命令依赖信息。
 */
export interface FeatureCommandDependencyInfo {
    /** 仅使后续特征重新求值的范围依赖，不构成删除级联。 */
    readonly rebuildReferencedModelIds?: readonly string[];
    /** 命令引用到的上游模型对象或特征 ID。 */
    readonly referencedModelIds: readonly string[];
    /** 命令产出的稳定对象 ID。 */
    readonly producedObjectIds: readonly string[];
}

/**
 * 生成命令时所需的上下文参数。
 */
export interface FeatureCommandBuildContext<TPrototype extends FeaturePrototype = FeaturePrototype> {
    /** 待序列化的特征原型。 */
    prototype: TPrototype;
    /** 当前客户端 ID。 */
    clientId: string;
    /** 命令时间戳。 */
    timestamp?: number;
}

/**
 * 特征命令适配器抽象基类。
 */
export abstract class FeatureCommandAdapter<TPrototype extends FeaturePrototype = FeaturePrototype> {
    /**
     * 判断当前命令是否由该适配器负责。
     *
     * @param command 命令对象。
     * @returns 是否支持。
     */
    public abstract supports(command: CadCommand): boolean;

    /**
     * 从命令历史恢复特征原型。
     *
     * @param command 命令对象。
     * @returns 特征原型；不支持时返回 null。
     */
    public abstract createPrototypeFromCommand(command: CadCommand): TPrototype | null;

    /**
     * 收集命令删除依赖信息。
     *
     * 设计说明：
     * 1. 删除计划器只消费适配器产出的统一依赖信息，不再手写命令字段白名单；
     * 2. 默认实现复用特征原型中的绑定声明，并从当前正式结果补充精确输入修订；
     * 3. 只有命令直接产出的结果才允许反向补充输入，避免 topology provenance 形成历史循环依赖；
     * 4. 具体特征若有额外输入引用，应在对应适配器中覆写并追加声明。
     *
     * @param context 依赖解析上下文。
     * @returns 命令依赖信息。
     */
    public collectDependencyInfo(
        context: FeatureCommandDependencyContext<TPrototype>,
    ): FeatureCommandDependencyInfo {
        const resultObjectId = readCommandStringParam(context.command, 'resultObjectId');
        const referencedModelIds = context.prototype
            .getBindings()
            .flatMap((binding) => binding.collectModelReferenceIds());
        const directResultReferencedModelIds = context.objects
            .filter((object) => isGeometryObjectDirectResultOfFeature(object, context.command.id))
            .flatMap((object) => collectGeometryObjectSourceReferenceIds(object));

        return {
            referencedModelIds: uniqueNonEmptyStrings([
                ...referencedModelIds,
                ...directResultReferencedModelIds,
            ]),
            producedObjectIds: uniqueNonEmptyStrings([
                context.command.id,
                resultObjectId,
                ...collectCurrentFeatureResultObjectIds(context.command.id, context.objects),
            ]),
        };
    }

    /**
     * 从特征原型构建命令对象。
     *
     * @param context 构建上下文。
     * @returns 命令对象。
     */
    public abstract createCommandFromPrototype(context: FeatureCommandBuildContext<TPrototype>): CadCommand;
}
